import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Edit3, Save, X, ArrowLeft, MessageCircle } from 'lucide-react';
import { authApi, UserProfileUpdate } from '../services/api';
import { useTranslation } from 'react-i18next';
import { MixedText } from '../utils/rtl';
import { DashboardContainer } from './layout/PageContainer';
import { SpaceCard, Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import UpgradeModal from './credits/UpgradeModal';
import CreditStatusCard from './credits/CreditStatusCard';

interface UserProfile {
  id: number;
  email: string;
  gender: string;
  created_at: string;
  updated_at: string;
}

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [editForm, setEditForm] = useState({
    gender: 'female'
  });
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const userData = await authApi.getCurrentUser();
      setProfile(userData);
      setEditForm({
        gender: userData.gender || 'female'
      });
    } catch (err: any) {
      setError(t('profile:errors.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Call API to update the profile
      const updatedUser = await authApi.updateProfile({
        gender: editForm.gender
      });
      
      setSuccess(t('profile:messages.update_success'));
      setEditing(false);
      
      // Update local profile state
      setProfile(updatedUser);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(t('profile:errors.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
    if (profile) {
      setEditForm({
        gender: profile.gender
      });
    }
  };

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (!profile) {
    return (
      <DashboardContainer>
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold text-bg-primary mb-2">
            🌌 {t('profile:errors.load_profile_error')}
          </h3>
          <Button onClick={() => navigate('/')} variant="primary">
            {t('common:actions.back_to_dashboard')}
          </Button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
        >
          {isRTL ? (
            <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          ) : (
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          )}
          <span className={`font-medium text-bg-secondary hover:text-vibrant-orange transition-colors ${isRTL ? 'text-right' : ''}`}>
            🚀 {t('common:actions.back_to_dashboard')}
          </span>
        </button>

        {/* Header */}
        <SpaceCard constellation="orion" className="group hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 group-hover:shadow-xl transition-shadow duration-300">
                <User className="w-8 h-8 text-vibrant-orange" />
              </div>
              <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                <CardTitle className={`text-3xl font-bold ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('profile:title')}</MixedText>
                </CardTitle>
                <CardContent className={`text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('profile:subtitle')}</MixedText>
                </CardContent>
              </div>
            </div>
          </CardHeader>
        </SpaceCard>

        {/* Messages */}
        {error && (
          <Card className="border-vibrant-orange/30 bg-vibrant-orange/10">
            <CardContent className={`text-vibrant-orange ${isRTL ? 'text-right' : ''}`}>
              ⚠️ {error}
            </CardContent>
          </Card>
        )}

        {success && (
          <Card className="border-green-500/30 bg-green-500/10">
            <CardContent className={`text-green-600 ${isRTL ? 'text-right' : ''}`}>
              ✅ {success}
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid - Profile on left, Credits on right */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8`}>
          {/* Profile Form - Left side (order-2 in RTL) */}
          <div className={isRTL ? 'lg:order-2' : 'lg:order-1'}>
            <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300 h-full flex flex-col">
              <CardHeader>
                <CardTitle className={`text-xl ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('profile:account_details')}</MixedText>
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-6 flex-1">
                {/* Email Field */}
                <div>
                  <label className={`block text-sm font-medium text-surface-secondary mb-2 ${isRTL ? 'text-right' : ''}`}>
                    📧 {t('profile:labels.email_address')}
                  </label>
                  <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'} p-3 bg-surface-muted/30 rounded-lg`}>
                    <Mail className="w-5 h-5 text-surface-muted flex-shrink-0" />
                    <span className={`text-surface-primary flex-1 ${isRTL ? 'text-right' : ''}`}>
                      {profile.email}
                    </span>
                  </div>
                </div>

                {/* Gender Field */}
                <div>
                  <label className={`block text-sm font-medium text-surface-secondary mb-3 ${isRTL ? 'text-right' : ''}`}>
                    👤 {t('profile:labels.gender')}
                  </label>
                  {editing ? (
                    <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} rounded-lg border border-surface-muted/30 overflow-hidden`}>
                      <button
                        type="button"
                        onClick={() => setEditForm({...editForm, gender: 'female'})}
                        className={`flex-1 py-3 px-4 text-sm font-medium transition-all duration-200 ${
                          editForm.gender === 'female'
                            ? 'bg-vibrant-orange text-white'
                            : 'bg-surface text-surface-secondary hover:bg-surface-muted/20'
                        }`}
                      >
                        {t('profile:gender.female')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({...editForm, gender: 'male'})}
                        className={`flex-1 py-3 px-4 text-sm font-medium transition-all duration-200 ${
                          editForm.gender === 'male'
                            ? 'bg-vibrant-orange text-white'
                            : 'bg-surface text-surface-secondary hover:bg-surface-muted/20'
                        }`}
                      >
                        {t('profile:gender.male')}
                      </button>
                    </div>
                  ) : (
                    <div className={`p-3 bg-surface-muted/30 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                      <span className={`text-surface-primary ${isRTL ? 'text-right' : ''} block`}>
                        {profile.gender === 'female' ? t('profile:gender.female') : t('profile:gender.male')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="space-y-4 pt-4 border-t border-surface-muted/20">
                  <div>
                    <label className={`block text-sm font-medium text-surface-secondary mb-1 ${isRTL ? 'text-right' : ''}`}>
                      📅 {t('profile:labels.member_since')}
                    </label>
                    <p className={`text-surface-muted ${isRTL ? 'text-right' : ''}`}>
                      {formatDate(profile.created_at)}
                    </p>
                  </div>
                  
                  {profile.updated_at !== profile.created_at && (
                    <div>
                      <label className={`block text-sm font-medium text-surface-secondary mb-1 ${isRTL ? 'text-right' : ''}`}>
                        🔄 {t('profile:labels.last_updated')}
                      </label>
                      <p className={`text-surface-muted ${isRTL ? 'text-right' : ''}`}>
                        {formatDate(profile.updated_at)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>

              {/* Action Buttons */}
              {editing ? (
                <CardFooter className={`flex ${isRTL ? 'flex-row-reverse' : ''} gap-3 pt-6 border-t border-surface-muted/20`}>
                  <Button
                    onClick={handleSave}
                    loading={saving}
                    variant="primary"
                    className="flex-1"
                    icon={<Save className="w-4 h-4" />}
                  >
                    {t('common:buttons.save_changes')}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    disabled={saving}
                    variant="secondary"
                    className="flex-1"
                    icon={<X className="w-4 h-4" />}
                  >
                    {t('common:buttons.cancel')}
                  </Button>
                </CardFooter>
              ) : (
                <CardFooter align="center" className="border-t border-surface-muted/20">
                  <button
                    onClick={() => setEditing(true)}
                    className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                    aria-label={t('profile:actions.edit_profile')}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </CardFooter>
              )}
            </SpaceCard>
          </div>

          {/* Credits Card - Right side (order-1 in RTL) */}
          <div className={isRTL ? 'lg:order-1' : 'lg:order-2'}>
            <CreditStatusCard onUpgradeClick={() => setShowUpgradeModal(true)} />
          </div>
        </div>

        {/* Customer Support CTA Card */}
        <SpaceCard constellation="ursa" className="group hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-vibrant-orange/5 to-muted-purple/5 border-vibrant-orange/20">
          <CardContent>
            <div className={`flex flex-col md:flex-row items-center gap-6 py-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
              {/* Astronaut Image */}
              <div className="flex-shrink-0 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-vibrant-orange/20 to-muted-purple/20 rounded-full blur-xl animate-pulse"></div>
                <img 
                  src="/astronaut_customer_support.png" 
                  alt="Customer Support" 
                  className="w-28 h-28 md:w-36 md:h-36 object-contain relative z-10"
                />
              </div>
              
              {/* Support Content */}
              <div className={`flex-1 text-center md:text-left ${isRTL ? 'md:text-right' : ''}`}>
                <h3 className={`text-2xl font-bold mb-3 ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('profile:support.title')}</MixedText>
                </h3>
                <p className={`text-surface-secondary text-lg mb-4 ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('profile:support.description')}</MixedText>
                </p>
                
                {/* CTA Button/Email */}
                <div className={`inline-flex items-center gap-3 bg-vibrant-orange/10 hover:bg-vibrant-orange/20 px-6 py-3 rounded-xl transition-all duration-300 group cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MessageCircle className="w-6 h-6 text-vibrant-orange group-hover:scale-110 transition-transform" />
                  <a 
                    href="mailto:support@study-bel.com" 
                    className="text-vibrant-orange hover:text-vibrant-orange/90 font-semibold text-lg transition-colors"
                  >
                    support@study-bel.com
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </SpaceCard>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        )}
      </div>
    </DashboardContainer>
  );
};

export default Profile; 